/* ORION feature arc (mattress page, "Χαρακτηριστικά και υλικά").
   The product's feature and material icons ride round an arc that stands on the bottom edge of the black section and
   turn to the right without end, slipping under the edge at its feet. A solid line runs between them and moves with
   them, breaking before each icon. Each icon grows from a little before the top, is largest right at the top, lit on
   its own lines, and is back to its size a little after it; its name rises into the middle of the arc letter by
   letter (blur in by character). Dragging turns the arc as far as it is pulled; pressing an icon lights it where it
   is and shows its name until it is let go. The button next to the title opens the full list.
   With data-cards (the refreshed mattress page), below 990 px the same items are large cards in a row instead: the card
   in the middle is lit and shows its name large, letter by letter as on the arc; the row moves on by itself one card at
   a time and has no end, a finger pulls it along, and a tap brings a card to the middle. The first time the cards are
   seen they all go by quickly, each whole for a moment, before the row settles into its pace. */
(() => {
  if (customElements.get('orion-feature-arc')) return;

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smooth = (edge0, edge1, x) => { const t = clamp((x - edge0) / (edge1 - edge0)); return t * t * (3 - 2 * t); };

  // ------------------------------------------------------------------------------------------------ Text effect
  const segmenter = typeof Intl !== 'undefined' && Intl.Segmenter ? new Intl.Segmenter('el', { granularity: 'grapheme' }) : null;
  const graphemes = word => (segmenter ? Array.from(segmenter.segment(word), part => part.segment) : Array.from(word));
  const measure = document.createElement('canvas').getContext('2d');
  const outQuad = u => 1 - (1 - u) * (1 - u);
  const outCubic = u => 1 - (1 - u) ** 3;
  const inQuad = u => u * u;
  // Each property keeps its own curve; the curves are baked into one keyframe list, so every letter runs a single
  // linear animation of opacity, transform and filter that the browser can play off the main thread.
  const bake = (curve, steps = 20) => Array.from({ length: steps + 1 }, (_, step) => {
    const u = step / steps;
    const { opacity, y, blur } = curve(u);
    return { offset: u, opacity: +opacity.toFixed(4), transform: `translate3d(0, ${y.toFixed(4)}em, 0)`, filter: `blur(${blur.toFixed(4)}em)` };
  });
  const IN = bake(u => ({ opacity: outQuad(clamp(u / .6)), y: .75 * (1 - outCubic(u)), blur: .35 * (1 - outQuad(clamp(u / .75))) }));
  const OUT = bake(u => ({ opacity: 1 - clamp(u / .9) ** 1.4, y: -.32 * inQuad(u), blur: .3 * outQuad(u) }));

  // Greek capitals carry no accents; a vowel that had one before ι or υ keeps them apart with a diaeresis (ΜΑΪΟΣ).
  const capitals = text => {
    const letters = text.normalize('NFD');
    let out = '';
    for (let i = 0; i < letters.length; i++) {
      const ch = letters[i];
      if (ch === '́') {
        const next = letters[i + 1];
        if (next && /[ιυΙΥ]/.test(next) && letters[i + 2] !== '̈') { out += `${next}̈`; i++; }
        continue;
      }
      out += ch;
    }
    return out.normalize('NFC').toLocaleUpperCase('el');
  };

  // Splits text into letters. Kerning is kept: what each pair of letters gains or loses when set together is measured
  // and given back as a margin, so the split word looks exactly like ordinary text.
  const split = (target, text) => {
    target.textContent = '';
    const style = getComputedStyle(target);
    const size = parseFloat(style.fontSize) || 16;
    measure.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    const width = value => measure.measureText(value).width;
    const chars = [];
    text.split(' ').forEach((word, position) => {
      if (position) target.append(' ');
      const wordEl = document.createElement('span');
      wordEl.className = 'orion-feature-arc__w';
      const letters = graphemes(word);
      letters.forEach((letter, index) => {
        const ch = document.createElement('span');
        ch.className = 'orion-feature-arc__c';
        ch.textContent = letter;
        const nextLetter = letters[index + 1];
        if (nextLetter) {
          const kern = width(letter + nextLetter) - width(letter) - width(nextLetter);
          if (Math.abs(kern) > .05) ch.style.marginRight = `${(kern / size).toFixed(4)}em`;
        }
        wordEl.append(ch);
        chars.push(ch);
      });
      target.append(wordEl);
    });
    return chars;
  };

  // The wave crosses the word at an even speed, line after line, whatever the width of each letter.
  const sweep = (chars, spread) => {
    const boxes = chars.map(ch => ({ top: ch.offsetTop, left: ch.offsetLeft, right: ch.offsetLeft + ch.offsetWidth }));
    const lines = [];
    boxes.forEach(box => {
      let line = lines.find(item => Math.abs(item.top - box.top) < 2);
      if (!line) lines.push(line = { top: box.top, left: box.left, right: box.right });
      line.left = Math.min(line.left, box.left);
      line.right = Math.max(line.right, box.right);
    });
    lines.sort((a, b) => a.top - b.top);
    let length = 0;
    lines.forEach(line => { line.start = length; length += line.right - line.left; });
    return boxes.map(box => {
      const line = lines.find(item => Math.abs(item.top - box.top) < 2);
      return (line.start + (box.left + box.right) / 2 - line.left) / (length || 1) * spread;
    });
  };
  const wave = (chars, keyframes, { duration, spread, fill, after = 0 }) => {
    const delays = sweep(chars, spread);
    return Promise.all(chars.map((ch, index) => ch.animate(keyframes, { duration, delay: after + delays[index], easing: 'linear', fill }).finished));
  };

  // ------------------------------------------------------------------------------------------------ The arc
  const STEP_MS = 3200;           // one place every 3.2 s, always to the right
  const BASE = 1 / STEP_MS;       // places per millisecond
  const PEAK = 1.4;               // size of an icon right at the top
  const REACH = .5;               // how far either side of the top (in places) an icon grows and shrinks
  // 1 right at the top, 0 from REACH places away: an icon grows, lights and shrinks back as it crosses the top.
  const bell = r => smooth(REACH, 0, Math.abs(r));
  let uid = 0;
  // The spotlit symbol (data-spot: Cool Max, owner 2026-10-01) shines a little more than the others. On the arc the turn
  // slows evenly as it nears the top and stops with it right on top; it rests there longer than the others take to pass,
  // while a circle of light goes round it, shines a moment and fades; then the arc turns on at its pace. As a card it
  // stays longer in the middle, with the same circle of light.
  const SPOT_BRAKE = .3;          // places before the top where the slowing starts
  const SPOT_HOLD_MS = 1900;      // the rest on top
  const RING_MS = 1500;           // the circle of light: drawn round from the top, whole a moment, then fading out
  const SPOT_DWELL_MS = 3400;     // a card's stay in the middle

  // The cards.
  const DWELL_MS = 1700;          // how long a card stays in the middle before the row moves on
  const MOVE_MS = 780;            // one card along
  const HOLD_MS = 6000;           // after a finger, a key or a tap the row waits this long before it moves by itself
  const INTRO_HOLD_MS = 400;      // the first time the cards are seen, each stays whole in the middle this long
  const INTRO_MOVE_MS = 320;      // and the row moves on this quickly
  const inOut = u => (u < .5 ? 4 * u * u * u : 1 - (-2 * u + 2) ** 3 / 2);

  class OrionFeatureArc extends HTMLElement {
    connectedCallback() {
      if (this.live) return;
      if (!this.built && !this.build()) return;
      this.live = true;
      this.listen();
      if (this.hasAttribute('data-cards')) {
        const media = window.matchMedia('(max-width: 989px)');
        const sync = () => this.setMode(media.matches ? 'cards' : 'arc');
        media.addEventListener('change', sync);
        this.cleanup.push(() => media.removeEventListener('change', sync));
        sync();
      } else {
        this.setMode('arc');
      }
      // The list button next to the title works only with this script, so it shows only now.
      const opener = document.querySelector(`[data-feature-list-open][aria-controls="${this.dataset.list}"]`);
      if (opener) opener.hidden = false;
    }

    // Once per element: the lit copy of each icon, the line and the starting state.
    build() {
      const items = [...this.querySelectorAll('.orion-feature-arc__item')];
      if (!items.length) return false;
      this.built = true;
      this.id ||= `OrionFeatureArc-${++uid}`;
      // The owner's rule: this motion plays also when the system asks for reduced motion.
      this.reduce = { matches: false };
      this.items = items.map((el, index) => {
        el.dataset.index = index;
        const base = el.querySelector('img');
        let lit = null;
        if (base) {
          base.classList.add('orion-feature-arc__icon');
          base.draggable = false;
          lit = base.cloneNode();
          lit.classList.add('orion-feature-arc__icon--lit');
          lit.alt = '';
          lit.setAttribute('aria-hidden', 'true');
          base.after(lit);
        }
        return { el, base, lit, title: el.dataset.title || el.getAttribute('aria-label') || '', kind: el.dataset.kind || '', spot: el.hasAttribute('data-spot'), last: {} };
      });
      this.spot = this.items.findIndex(item => item.spot);
      this.spotState = 'idle';      // idle, brake (slowing for the top), hold (resting on top), gone (turning on)
      this.spotUntil = 0;
      this.spotAt = null;
      this.ringStart = 0;
      this.buildLine();
      this.kindBox = this.querySelector('.orion-feature-arc__kind');
      this.wordBox = this.querySelector('.orion-feature-arc__word');
      this.status = this.querySelector('[data-arc-status]');
      this.slots = [];
      this.shownKind = '';
      this.offset = 0;              // item i sits on top when offset = i (mod the number of items)
      this.velocity = 0;            // places per millisecond; eases back to BASE
      this.boost = 0;               // extra speed from scrolling the page
      this.visible = false;
      this.entered = false;
      this.pressed = null;
      this.pressGlow = 0;
      this.pressTarget = 0;
      this.pressUntil = 0;
      this.shown = -1;
      this.lastSwitch = 0;
      this.hover = null;
      this.gesture = null;
      this.frame = 0;
      this.lastTime = 0;
      this.classList.add('is-ready');
      return true;
    }

    disconnectedCallback() {
      if (!this.live) return;
      this.live = false;
      cancelAnimationFrame(this.frame);
      clearTimeout(this.cardTimer);
      this.frame = 0;
      this.gesture = null;
      this.cleanup.forEach(stop => stop());
    }

    // The line is drawn on a canvas in the same frame as the icons, so its breaks always sit round them.
    buildLine() {
      const canvas = document.createElement('canvas');
      canvas.className = 'orion-feature-arc__line';
      canvas.setAttribute('aria-hidden', 'true');
      this.prepend(canvas);
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
    }

    listen() {
      const on = (target, type, handler, options) => { target.addEventListener(type, handler, options); return () => target.removeEventListener(type, handler, options); };
      const resize = new ResizeObserver(() => this.layout());
      resize.observe(this);
      const seen = new IntersectionObserver(entries => {
        this.visible = entries[entries.length - 1].isIntersecting;
        // The first time the arc comes into view the icons sweep in and settle into their pace, with the first of
        // them (Ορθοπεδικό when the mattress has it) arriving on top.
        if (this.visible && !this.entered && this.mode === 'arc') {
          this.entered = true;
          if (!this.reduce.matches) {
            this.offset -= 2.2;
            this.velocity = BASE * 14;
            this.render();
          }
        }
        this.wake();
      }, { rootMargin: '80px 0px' });
      seen.observe(this);
      let lastScroll = window.scrollY;
      this.cleanup = [
        () => resize.disconnect(),
        () => seen.disconnect(),
        on(document, 'visibilitychange', () => this.wake()),
        // Scrolling the page gives the arc a little push, so it answers to the visitor.
        on(window, 'scroll', () => {
          const y = window.scrollY;
          const moved = Math.abs(y - lastScroll);
          lastScroll = y;
          if (!this.visible || this.reduce.matches || this.mode !== 'arc') return;
          this.boost = Math.min(2.2, this.boost + Math.min(moved, 160) * .004);
          this.wake();
        }, { passive: true }),
        on(this, 'pointerdown', event => this.pointerDown(event)),
        on(this, 'pointermove', event => this.pointerMove(event)),
        on(this, 'pointerup', event => this.pointerEnd(event, false)),
        on(this, 'pointercancel', event => this.pointerEnd(event, true)),
        // Only the arc's own capture ending counts: a finger starts captured by the icon it lands on, and handing that
        // capture over to the arc for a drag makes the icon report a lost capture, which must not end the drag.
        on(this, 'lostpointercapture', event => { if (event.target === this) this.pointerEnd(event, true); }),
        on(this, 'pointerleave', () => { this.hover = null; this.wake(); }),
        on(this, 'dragstart', event => event.preventDefault()),
        on(this, 'keydown', event => this.keyDown(event)),
        on(this, 'focusin', event => {
          const item = event.target.closest('.orion-feature-arc__item');
          if (!item || !item.matches(':focus-visible')) return;
          if (this.mode === 'cards') this.goTo(Number(item.dataset.index), true);
          else this.startPress(Number(item.dataset.index), true);
        }),
        on(this, 'focusout', event => { if (this.mode === 'arc' && event.target.closest('.orion-feature-arc__item')) this.releasePress(0); }),
        on(this, 'click', event => {
          const step = event.target.closest('[data-card-step]');
          if (step) { this.goTo(Number(step.dataset.cardStep), true); return; }
          const item = event.target.closest('.orion-feature-arc__item');
          if (!item || event.detail !== 0) return;
          if (this.mode === 'cards') { this.goTo(Number(item.dataset.index), true); return; }
          this.startPress(Number(item.dataset.index), true);
          this.releasePress(1600);
        })
      ];
    }

    layout() {
      if (this.mode === 'cards') { this.layoutCards(); return; }
      const W = this.clientWidth;
      const H = this.clientHeight;
      if (!W || !H) return;
      const wide = W >= 700;
      const S = Math.round(clamp(W * .09, 58, 112));
      const apexY = S * PEAK / 2 + 20;
      // The arc stands on the section's bottom edge: both feet land on it well inside the screen, so no icon is ever
      // cut at the sides, and the icons slip under the edge at the feet. A circle while it fits; on narrow screens a
      // taller arch (half an ellipse), so there is room for the name inside it.
      const b = Math.max(S, H - apexY);
      const cx = W / 2;
      const room = W / 2 - S * .5 - (wide ? 18 : 8);
      // Past the feet the path carries on until an icon has gone fully under the edge; on a circle it still widens
      // a little there, so the feet are drawn in until even that stays clear of the sides.
      let a = Math.max(S, room), foot, Rx, Ry, cy, end;
      for (let pass = 0; pass < 3; pass++) {
        foot = Math.min(2 * Math.atan(b / a), Math.PI / 2);
        Rx = a / Math.sin(foot);
        Ry = b / (1 - Math.cos(foot));
        cy = apexY + Ry;
        const cosEnd = (cy - H - S * .62) / Ry;
        end = cosEnd <= -1 ? Math.PI : Math.acos(clamp(cosEnd, -1, 1));
        const widest = Rx * (end > Math.PI / 2 ? 1 : Math.sin(end));
        if (widest <= room + .5) break;
        a = Math.max(S, a - (widest - room));
      }
      // Length along the arch, so the icons keep even gaps and an even speed whatever its shape.
      const steps = 360;
      const thetas = new Float64Array(steps + 1);
      const lengths = new Float64Array(steps + 1);
      for (let k = 1; k <= steps; k++) {
        const from = end * (k - 1) / steps, to = end * k / steps;
        thetas[k] = to;
        lengths[k] = lengths[k - 1] + Math.hypot(Rx * (Math.sin(to) - Math.sin(from)), Ry * (Math.cos(from) - Math.cos(to)));
      }
      const sEnd = lengths[steps];
      const N = this.items.length;
      let gap = Math.max(S * 1.9, 2 * sEnd / (wide ? 5.2 : 3.2));
      gap = Math.max(gap, (2 * sEnd + gap * .35) / Math.max(1, N - 1));
      // The name sits inside the arch, below the icon on top.
      const top = apexY + S * PEAK / 2;
      const labelY = top + (H - top) * (wide ? .46 : .6);
      // It stays clear of the icons passing the sides of the arch, up to the top of its letters (the arch narrows
      // going up), and its longest word always fits on one line.
      const inside = y => { const c = clamp((cy - y) / Ry, -1, 1); return Rx * Math.sqrt(1 - c * c) - S * .5 - (wide ? 26 : 16); };
      const longest = Math.max(...this.items.map(item => Math.max(...capitals(item.title).split(' ').map(word => word.length))), 6);
      let labelSize = Math.min(wide ? clamp(W * .046, 34, 60) : clamp(W * .066, 22, 30), 2 * inside(labelY) / (longest * .74));
      const labelW = Math.max(120, Math.min(2 * inside(labelY - labelSize * .8), wide ? 760 : W - 40));
      labelSize = Math.min(labelSize, labelW / (longest * .74));
      this.geo = { W, H, S, Rx, Ry, cx, cy, apexY, gap, sEnd, thetas, lengths, steps };
      // Icons are laid out at their largest size and scaled down, so they stay sharp when they grow.
      this.style.setProperty('--ofa-s', `${Math.round(S * PEAK)}px`);
      this.style.setProperty('--ofa-label-y', `${labelY.toFixed(1)}px`);
      this.style.setProperty('--ofa-label-w', `${labelW.toFixed(1)}px`);
      this.style.setProperty('--ofa-label-size', `${labelSize.toFixed(1)}px`);
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      this.canvas.width = Math.round(W * dpr);
      this.canvas.height = Math.round(H * dpr);
      this.canvas.style.width = `${W}px`;
      this.canvas.style.height = `${H}px`;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // The line is lit near the top, where the icons are lit.
      const glow = this.ctx.createRadialGradient(cx, apexY, 0, cx, apexY, S * 2.8);
      glow.addColorStop(0, 'rgba(255, 244, 222, .95)');
      glow.addColorStop(.45, 'rgba(234, 210, 162, .38)');
      glow.addColorStop(1, 'rgba(214, 179, 106, 0)');
      this.lineGlow = glow;
      this.items.forEach(item => { item.last = {}; });
      this.render();
      if (this.shown >= 0) this.relabel();
    }

    // Angle on the arch for a distance along it from the top (negative to the left).
    thetaAt(s) {
      const { thetas, lengths, steps } = this.geo;
      const d = Math.min(Math.abs(s), lengths[steps]);
      let lo = 0, hi = steps;
      while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (lengths[mid] < d) lo = mid; else hi = mid; }
      const span = lengths[hi] - lengths[lo] || 1;
      const theta = thetas[lo] + (thetas[hi] - thetas[lo]) * (d - lengths[lo]) / span;
      return s < 0 ? -theta : theta;
    }

    // Place of item i relative to the top, in places (0 on top, above 0 to the right).
    relative(index) {
      const N = this.items.length;
      let r = (this.offset - index) % N;
      if (r < -N / 2) r += N;
      if (r >= N / 2) r -= N;
      return r;
    }

    render() {
      const geo = this.geo;
      if (!geo) return;
      const { S, Rx, Ry, cx, cy, gap, sEnd } = geo;
      const box = S * PEAK;
      const breaks = [];
      let lit = 0;
      this.items.forEach((item, index) => {
        const r = this.relative(index);
        const s = r * gap;
        const onArc = Math.abs(s) < sEnd;
        const theta = this.thetaAt(s);
        const x = cx + Rx * Math.sin(theta);
        const y = cy - Ry * Math.cos(theta);
        let glow = bell(r);
        if (this.pressed !== null) {
          glow = index === this.pressed ? Math.max(glow * (1 - this.pressGlow), this.pressGlow) : glow * (1 - .8 * this.pressGlow);
        }
        lit = Math.max(lit, glow);
        // Icons near the mouse brighten a little.
        let near = 0;
        if (this.hover && onArc) near = smooth(S * 1.7, S * .45, Math.hypot(this.hover.x - x, this.hover.y - y));
        const size = 1 + (PEAK - 1) * glow + .05 * near * (1 - glow);
        this.write(item, x - box / 2, y - box / 2, size / PEAK, onArc ? 1 : 0, glow, near);
        if (index === this.spot) this.spotAt = { x, y, size };
        // The line stops well before the icon, however large it has grown.
        if (onArc) {
          const radius = S * .46 * size + 16;
          breaks.push([s - radius, s + radius]);
        }
      });
      this.drawLine(breaks, lit);
      if (this.ringStart) this.drawRing(performance.now());
    }

    // The circle of light round the spotlit symbol, on the line's canvas (inside the line's break round the symbol): a
    // light goes round it from the top, the circle shines whole a moment, then fades out as it widens a little.
    drawRing(now) {
      const p = (now - this.ringStart) / RING_MS;
      const at = this.spotAt;
      if (p >= 1 || !at) { this.ringStart = 0; return; }
      const ctx = this.ctx;
      const sweep = 1 - (1 - clamp(p / .36)) ** 3;
      const fade = 1 - smooth(.52, 1, p);
      const radius = this.geo.S * at.size * .5 * (1.02 + .08 * smooth(.45, 1, p));
      const start = -Math.PI / 2, end = start + Math.PI * 2 * sweep;
      ctx.save();
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#fff';
      ctx.globalAlpha = .14 * fade;
      ctx.lineWidth = 7;
      ctx.beginPath(); ctx.arc(at.x, at.y, radius, start, end); ctx.stroke();
      ctx.globalAlpha = .92 * fade;
      ctx.lineWidth = 1.5;
      ctx.shadowColor = 'rgba(255, 255, 255, .9)';
      ctx.shadowBlur = 12;
      ctx.beginPath(); ctx.arc(at.x, at.y, radius, start, end); ctx.stroke();
      if (sweep < 1) {
        ctx.fillStyle = '#fff';
        ctx.shadowBlur = 16;
        ctx.beginPath(); ctx.arc(at.x + radius * Math.cos(end), at.y + radius * Math.sin(end), 2.4, 0, Math.PI * 2); ctx.fill();
      }
      ctx.restore();
    }

    drawLine(breaks, lit) {
      const { W, H, Rx, Ry, cx, cy, sEnd } = this.geo;
      const ctx = this.ctx;
      ctx.clearRect(0, 0, W, H);
      breaks.sort((a, b) => a[0] - b[0]);
      const path = new Path2D();
      const piece = (from, to) => {
        if (to - from < 1) return;
        const a = this.thetaAt(from), b = this.thetaAt(to);
        path.moveTo(cx + Rx * Math.sin(a), cy - Ry * Math.cos(a));
        path.ellipse(cx, cy, Rx, Ry, 0, a - Math.PI / 2, b - Math.PI / 2);
      };
      let start = -sEnd;
      breaks.forEach(([from, to]) => {
        if (to <= start) return;
        if (from > start) piece(start, Math.min(from, sEnd));
        start = Math.max(start, to);
      });
      if (start < sEnd) piece(start, sEnd);
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(244, 243, 239, .24)';
      ctx.stroke(path);
      ctx.globalAlpha = .35 + .65 * lit;
      ctx.lineWidth = 1.4;
      ctx.strokeStyle = this.lineGlow;
      ctx.stroke(path);
      ctx.globalAlpha = 1;
    }

    write(item, x, y, scale, alpha, glow, near) {
      const last = item.last;
      const transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) scale(${scale.toFixed(4)})`;
      if (last.transform !== transform) { item.el.style.transform = transform; last.transform = transform; }
      const a = alpha.toFixed(3);
      if (last.alpha !== a) {
        item.el.style.opacity = a;
        // Under the edge the icon keeps its name for screen readers but cannot be reached with Tab or the pointer.
        const hidden = alpha < .02;
        if (hidden !== last.hidden) {
          item.el.style.pointerEvents = hidden ? 'none' : '';
          item.el.tabIndex = hidden ? -1 : 0;
          last.hidden = hidden;
        }
        last.alpha = a;
      }
      const g = glow.toFixed(3);
      if (last.glow !== g && item.lit) { item.lit.style.opacity = g; last.glow = g; }
      const b = (item.spot ? .8 + .2 * near : .5 + .3 * near).toFixed(3);
      if (last.base !== b && item.base) { item.base.style.opacity = b; last.base = b; }
    }

    nearest() {
      const N = this.items.length;
      return ((Math.round(this.offset) % N) + N) % N;
    }

    // The word in the middle belongs to the icon crossing the top (the one growing), or to the icon being pressed.
    updateWord(now) {
      let want;
      const pressing = this.pressed !== null && this.pressTarget > 0;
      if (pressing) want = this.pressed;
      else if (this.shown >= 0 && this.pressed === null && Math.abs(this.relative(this.shown)) < .53) want = this.shown;
      else want = this.nearest();
      if (want === this.shown) return;
      // While the arc spins fast (sweeping in, flung) the word waits for it to calm down.
      if (!pressing && (Math.abs(this.velocity) > BASE * 2.6 || now - this.lastSwitch < 240)) return;
      this.shown = want;
      this.lastSwitch = now;
      const item = this.items[want];
      this.showWord(capitals(item.title), item.kind);
    }

    relabel() {
      // After a resize the word is set again at its new size.
      this.slots.forEach(slot => slot.el.remove());
      this.slots = [];
      const item = this.items[this.shown];
      if (item) this.showWord(capitals(item.title), item.kind, true);
    }

    showWord(text, kind, instant = false) {
      const latest = this.slots[this.slots.length - 1];
      if (latest && latest.text === text && !latest.leaving) return;
      this.slots.forEach(slot => {
        if (slot.leaving) return;
        slot.leaving = true;
        const leave = slot.settled
          ? wave(slot.chars, OUT, { duration: 520, spread: Math.min(360, slot.chars.length * 22), fill: 'forwards' })
          : slot.el.animate([{ opacity: 1, filter: 'blur(0)' }, { opacity: 0, filter: 'blur(.12em)' }], { duration: 240, easing: 'ease-in', fill: 'forwards' }).finished;
        leave.catch(() => {}).then(() => { slot.el.remove(); this.slots = this.slots.filter(item => item !== slot); });
      });
      while (this.slots.length > 2) this.slots.shift().el.remove();
      const el = document.createElement('span');
      el.className = 'orion-feature-arc__slot';
      this.wordBox.append(el);
      const slot = { el, text, chars: split(el, text), leaving: false, settled: instant };
      this.slots.push(slot);
      if (!instant) {
        wave(slot.chars, IN, { duration: 800, spread: Math.min(560, slot.chars.length * 30), fill: 'backwards', after: this.slots.length > 1 ? 170 : 0 })
          .then(() => { slot.settled = true; }, () => {});
      }
      if (kind !== this.shownKind) {
        this.shownKind = kind;
        [...this.kindBox.children].forEach(old => old.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 220, fill: 'forwards' }).finished.then(() => old.remove(), () => {}));
        const tag = document.createElement('span');
        tag.textContent = kind;
        this.kindBox.append(tag);
        if (!instant) tag.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 420, delay: 160, fill: 'backwards' });
      }
    }

    tick(now) {
      if (this.mode === 'cards') { this.tickCards(now); return; }
      this.frame = 0;
      if (!this.live) return;
      const dt = Math.min(64, now - (this.lastTime || now));
      this.lastTime = now;
      const running = this.visible && !document.hidden;
      if (!this.gesture || this.gesture.state !== 'drag') {
        // Always turning to the right; only a finger or the mouse holding an icon stops it, and the spotlit symbol
        // rests a while on top.
        const held = this.pressed !== null && this.pressTarget > 0;
        if (held) this.spotState = 'idle';
        if (held || !this.spotStep(now, dt)) {
          const target = held ? 0 : BASE * (1 + this.boost);
          this.velocity += (target - this.velocity) * (1 - Math.exp(-dt / (held ? 140 : 520)));
          this.offset += this.velocity * dt;
        }
        this.boost *= Math.exp(-dt / 700);
      } else {
        this.spotState = 'idle';
      }
      if (this.pressed !== null) {
        if (this.pressTarget > 0 && this.pressUntil && now >= this.pressUntil) { this.pressTarget = 0; this.pressUntil = 0; }
        this.pressGlow += (this.pressTarget - this.pressGlow) * (1 - Math.exp(-dt / 150));
        if (this.pressTarget === 0 && this.pressGlow < .005) { this.pressed = null; this.pressGlow = 0; }
      }
      this.render();
      this.updateWord(now);
      if (running) this.frame = requestAnimationFrame(time => this.tick(time));
    }

    // The spotlit symbol on the arc: while it is brought to rest on top or rests there, this moves the arc and returns
    // true. It is brought in only at the arc's own pace (not flung, sweeping in or pushed hard by a scroll), with an
    // even slowing that ends exactly on top; it is not brought in again until it has gone well past.
    spotStep(now, dt) {
      if (this.spot < 0 || !this.entered) return false;
      const r = this.relative(this.spot);
      if (this.spotState === 'idle') {
        const left = -r, v = this.velocity;
        if (left > .002 && left < SPOT_BRAKE && v > 0 && v * v * SPOT_BRAKE <= 2.2 * left * BASE * BASE) this.spotState = 'brake';
        else return false;
      }
      if (this.spotState === 'brake') {
        const left = -r, v = this.velocity;
        const slow = v * v / (2 * Math.max(left, 1e-6));
        if (left <= .0005 || v <= 0 || dt >= v / slow) {
          this.offset += Math.max(0, left);
          this.velocity = 0;
          this.spotState = 'hold';
          this.spotUntil = now + SPOT_HOLD_MS;
          this.ringStart = now;
          return true;
        }
        this.offset += v * dt - slow * dt * dt / 2;
        this.velocity = v - slow * dt;
        return true;
      }
      if (this.spotState === 'hold') {
        if (now < this.spotUntil) { this.velocity = 0; return true; }
        this.spotState = 'gone';
        return false;
      }
      if (r > .6 || r < -SPOT_BRAKE - .3) this.spotState = 'idle';
      return false;
    }

    // Starts the frame loop when it is idle; the loop stops by itself while the arc is out of view.
    wake() {
      if (this.live && !this.frame && this.visible && !document.hidden) {
        this.lastTime = 0;
        this.frame = requestAnimationFrame(time => this.tick(time));
      }
    }

    // -------------------------------------------------------------------------------------------- Pointer and keys
    pointerDown(event) {
      if (this.mode === 'cards') { this.cardsDown(event); return; }
      if (!this.geo || event.button !== 0) return;
      const itemEl = event.target.closest('.orion-feature-arc__item');
      const gesture = {
        id: event.pointerId, x: event.clientX, y: event.clientY, lastX: event.clientX, start: performance.now(), state: 'pending',
        item: itemEl ? Number(itemEl.dataset.index) : null, samples: [], timer: 0
      };
      this.gesture = gesture;
      // A finger resting on an icon lights it where it is.
      if (gesture.item !== null) {
        gesture.timer = setTimeout(() => {
          if (this.gesture === gesture && gesture.state === 'pending') { gesture.state = 'press'; this.startPress(gesture.item, true); }
        }, 90);
      }
    }

    pointerMove(event) {
      if (this.mode === 'cards') { this.cardsMove(event); return; }
      if (event.pointerType === 'mouse' && !this.gesture) {
        const box = this.getBoundingClientRect();
        this.hover = { x: event.clientX - box.left - this.clientLeft, y: event.clientY - box.top - this.clientTop };
        this.wake();
      }
      const gesture = this.gesture;
      if (!gesture || event.pointerId !== gesture.id) return;
      if (gesture.state === 'pending' || gesture.state === 'press') {
        const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
        if (Math.hypot(dx, dy) < 7) return;
        if (event.pointerType !== 'mouse' && Math.abs(dy) > Math.abs(dx)) return;
        clearTimeout(gesture.timer);
        if (gesture.state === 'press') this.releasePress(0);
        gesture.state = 'drag';
        gesture.lastX = event.clientX;
        this.setPointerCapture(event.pointerId);
        this.classList.add('is-dragging');
        this.hover = null;
        this.wake();
        return;
      }
      if (gesture.state !== 'drag') return;
      // Sideways movement turns the arc one to one: the icons under the finger follow it.
      this.offset += (event.clientX - gesture.lastX) / this.geo.gap;
      gesture.lastX = event.clientX;
      const now = performance.now();
      gesture.samples.push({ t: now, offset: this.offset });
      while (gesture.samples.length > 2 && now - gesture.samples[0].t > 90) gesture.samples.shift();
      this.wake();
    }

    pointerEnd(event, cancelled) {
      if (this.mode === 'cards') { this.cardsUp(event, cancelled); return; }
      const gesture = this.gesture;
      if (!gesture || event.pointerId !== gesture.id) return;
      clearTimeout(gesture.timer);
      this.gesture = null;
      this.classList.remove('is-dragging');
      if (gesture.state === 'drag') {
        // Let go: the arc keeps the speed it was flung with and eases back into its own pace.
        const { samples } = gesture;
        let velocity = 0;
        if (!cancelled && samples.length > 1) {
          const first = samples[0], last = samples[samples.length - 1];
          if (performance.now() - last.t < 60 && last.t > first.t) velocity = (last.offset - first.offset) / (last.t - first.t);
        }
        this.velocity = clamp(velocity, -BASE * 22, BASE * 22);
      } else if (gesture.state === 'press') {
        // A short tap keeps the icon lit long enough to read its name; a long press lets go straight away.
        this.releasePress(!cancelled && performance.now() - gesture.start < 450 ? 1600 : 0);
      } else if (gesture.state === 'pending' && gesture.item !== null && !cancelled) {
        this.startPress(gesture.item, true);
        this.releasePress(1600);
      }
      this.wake();
    }

    startPress(index, announce) {
      this.pressed = index;
      this.pressTarget = 1;
      this.pressUntil = 0;
      if (announce && this.status) this.status.textContent = this.items[index].title;
      this.wake();
    }

    releasePress(linger) {
      if (this.pressed === null) return;
      if (linger) this.pressUntil = performance.now() + linger;
      else { this.pressTarget = 0; this.pressUntil = 0; }
      this.wake();
    }

    keyDown(event) {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      if (this.mode === 'cards') { this.goTo(this.nearestCard() + (event.key === 'ArrowRight' ? 1 : -1), true); return; }
      // A nudge of about one place either way; the arc then eases back into its pace (a rest on top ends at once).
      if (this.spotState === 'brake' || this.spotState === 'hold') this.spotState = 'gone';
      this.velocity += (event.key === 'ArrowRight' ? 1 : -1) / 520;
      this.wake();
    }

    // ---------------------------------------------------------------------------------------------------- Modes
    setMode(mode) {
      if (this.mode === mode) return;
      if (this.mode === 'cards') this.leaveCards();
      this.mode = mode;
      cancelAnimationFrame(this.frame);
      clearTimeout(this.cardTimer);
      this.frame = 0;
      this.gesture = null;
      this.pressed = null;
      this.pressGlow = 0;
      this.classList.remove('is-dragging');
      this.classList.toggle('is-ready', mode === 'arc');
      this.classList.toggle('is-cards', mode === 'cards');
      // Whatever the other mode wrote on the items goes.
      this.items.forEach(item => {
        ['transform', 'opacity', 'pointerEvents'].forEach(name => { item.el.style[name] = ''; });
        item.el.tabIndex = 0;
        if (item.base) item.base.style.opacity = '';
        if (item.lit) item.lit.style.opacity = '';
        item.last = {};
      });
      this.slots.forEach(slot => slot.el.remove());
      this.slots = [];
      this.shown = -1;
      this.shownKind = '';
      this.kindBox.textContent = '';
      if (mode === 'cards') this.enterCards();
      this.layout();
      this.wake();
    }

    // ---------------------------------------------------------------------------------------------------- Cards
    // Each item becomes a card: its icon, its kind and its name (drawn letter by letter when the card is in the
    // middle). Below the row, a step for each card; the step of the card in the middle fills while it waits.
    enterCards() {
      this.pos = 0;
      this.move = null;
      this.nextAt = 0;
      this.pauseUntil = 0;
      this.shownCard = -1;
      const count = this.items.length;
      this.items.forEach(item => {
        const tag = document.createElement('span');
        tag.className = 'orion-feature-arc__tag';
        tag.setAttribute('aria-hidden', 'true');
        tag.textContent = item.kind;
        const title = document.createElement('span');
        title.className = 'orion-feature-arc__title';
        title.setAttribute('aria-hidden', 'true');
        item.el.append(tag, title);
        item.card = { tag, title, chars: [] };
      });
      const steps = document.createElement('div');
      steps.className = 'orion-feature-arc__steps';
      steps.hidden = count < 2;
      this.items.forEach((item, index) => {
        const step = document.createElement('button');
        step.type = 'button';
        step.className = 'orion-feature-arc__step';
        step.dataset.cardStep = index;
        step.setAttribute('aria-label', `${index + 1} / ${count}: ${item.title}`);
        step.innerHTML = '<span><span></span></span>';
        steps.append(step);
      });
      this.append(steps);
      this.steps = [...steps.children];
      // The spotlit card's circle of light (ringCard).
      const spot = this.items[this.spot];
      if (spot) {
        const ring = document.createElement('span');
        ring.className = 'orion-feature-arc__ring';
        ring.setAttribute('aria-hidden', 'true');
        ring.innerHTML = '<svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="47" pathLength="100"></circle></svg>';
        spot.el.append(ring);
        spot.card.ring = ring;
      }
      // Once per page, when the cards are well in view, they all go by quickly (startIntro).
      if (!this.introDone && count > 1 && 'IntersectionObserver' in window) {
        this.introWatch = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.introWatch.disconnect();
          this.introWatch = null;
          this.introDone = true;
          this.startIntro();
        }, { threshold: .6 });
        this.introWatch.observe(this);
      }
    }

    // The first time the cards are seen they all go by quickly, each one whole in the middle for a moment, round to the
    // first again; from there the row keeps its own pace. A finger, a key or a tap ends it at once.
    startIntro() {
      if (this.mode !== 'cards' || this.items.length < 2) return;
      this.intro = { moves: this.items.length };
      this.pauseUntil = 0;
      if (!this.move) this.settle(performance.now());
      this.wake();
    }

    leaveCards() {
      clearTimeout(this.cardTimer);
      this.introWatch?.disconnect();
      this.introWatch = null;
      this.intro = null;
      this.fill?.cancel();
      this.fill = null;
      this.items.forEach(item => {
        item.el.classList.remove('is-current');
        item.card?.tag.remove();
        item.card?.title.remove();
        item.card?.ring?.remove();
        item.card = null;
      });
      this.steps?.[0]?.parentElement.remove();
      this.steps = null;
      this.cardGeo = null;
    }

    layoutCards() {
      const W = this.clientWidth;
      const H = this.clientHeight;
      if (!W || !H || !this.items[0]?.card) return;
      const phone = W < 600;
      const cardW = Math.round(phone ? W * .8 : clamp(W * .54, 320, 440));
      const cardH = Math.round(H - 64);
      // The name is as large as the longest word of any name allows on one line of the card.
      const style = getComputedStyle(this.items[0].card.title);
      measure.font = `${style.fontStyle} ${style.fontWeight} 100px ${style.fontFamily}`;
      const widest = Math.max(1, ...this.items.flatMap(item => item.title.split(' ').map(word => measure.measureText(word).width)));
      const name = Math.min(phone ? 46 : 58, (cardW - 48) / widest * 100 * .96);
      this.cardGeo = { W, cardW, spacing: cardW + (phone ? 14 : 22) };
      this.style.setProperty('--ofc-w', `${cardW}px`);
      this.style.setProperty('--ofc-h', `${cardH}px`);
      this.style.setProperty('--ofc-icon', `${Math.round(Math.min(cardW * .42, cardH * .36))}px`);
      this.style.setProperty('--ofc-name', `${name.toFixed(1)}px`);
      this.items.forEach(item => { item.last = {}; });
      this.renderCards();
      // After a resize the name in the middle is set again at its new size.
      const shown = this.items[this.shownCard];
      if (shown?.card) shown.card.chars = split(shown.card.title, shown.title);
    }

    // Where card i sits against the middle, in cards (to the right above 0); the row wraps round without end.
    cardPlace(index) {
      const N = this.items.length;
      let r = (index - this.pos) % N;
      if (r < -N / 2) r += N;
      if (r >= N / 2) r -= N;
      return r;
    }

    nearestCard() {
      const N = this.items.length;
      return ((Math.round(this.pos) % N) + N) % N;
    }

    renderCards() {
      const geo = this.cardGeo;
      if (!geo) return;
      const N = this.items.length;
      const middle = (geo.W - geo.cardW) / 2;
      this.items.forEach((item, index) => {
        const r = this.cardPlace(index);
        const d = Math.min(1, Math.abs(r));
        // A card far round the row fades out before it wraps to the other side, so no card ever jumps in view.
        const edge = N > 2 ? smooth(N / 2, N / 2 - .6, Math.abs(r)) : 1;
        const last = item.last;
        const transform = `translate3d(${(middle + r * geo.spacing).toFixed(2)}px, 0, 0) scale(${(1 - .1 * d).toFixed(4)})`;
        if (last.transform !== transform) { item.el.style.transform = transform; last.transform = transform; }
        const alpha = ((1 - .62 * d) * edge).toFixed(3);
        if (last.alpha !== alpha) {
          item.el.style.opacity = alpha;
          const hidden = alpha < .02;
          if (hidden !== last.hidden) {
            item.el.style.pointerEvents = hidden ? 'none' : '';
            item.el.tabIndex = hidden ? -1 : 0;
            last.hidden = hidden;
          }
          last.alpha = alpha;
        }
        const glow = bell(r).toFixed(3);
        if (last.glow !== glow && item.lit) { item.lit.style.opacity = glow; last.glow = glow; }
      });
    }

    // The card that has come to the middle shows its name, letter by letter; the one before lets its name go. While the
    // cards go by quickly the first time, a name is there whole at once and goes at once.
    showCard(index) {
      if (this.shownCard === index) return;
      const quick = !!this.intro;
      const old = this.items[this.shownCard];
      if (old?.card) {
        old.el.classList.remove('is-current');
        const chars = old.card.chars;
        if (quick) old.card.title.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 140, easing: 'ease-in', fill: 'forwards' });
        else if (chars.length) wave(chars, OUT, { duration: 420, spread: Math.min(300, chars.length * 18), fill: 'forwards' }).catch(() => {});
      }
      this.shownCard = index;
      const item = this.items[index];
      item.el.classList.add('is-current');
      item.card.title.getAnimations().forEach(animation => animation.cancel());
      item.card.chars = split(item.card.title, item.title);
      if (quick) item.card.title.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 160, easing: 'ease-out' });
      else wave(item.card.chars, IN, { duration: 800, spread: Math.min(560, item.card.chars.length * 30), fill: 'backwards' }).catch(() => {});
      this.steps?.forEach((step, n) => { if (n === index) step.setAttribute('aria-current', 'true'); else step.removeAttribute('aria-current'); });
    }

    moveTo(target, now, duration, ease) {
      this.move = { from: this.pos, to: target, start: now, duration, ease };
      this.nextAt = 0;
      this.fill?.cancel();
      this.fill = null;
    }

    // At rest in the middle: the row waits, and the card's step fills for as long as it waits. The spotlit card waits
    // longer, with its circle of light.
    settle(now) {
      const N = this.items.length;
      this.pos = ((Math.round(this.pos) % N) + N) % N;
      const spot = !this.intro && this.pos === this.spot;
      this.nextAt = Math.max(now, this.pauseUntil) + (this.intro ? INTRO_HOLD_MS : spot ? SPOT_DWELL_MS : DWELL_MS);
      this.fill?.cancel();
      const bar = this.steps?.[this.pos]?.querySelector('span span');
      this.fill = bar && N > 1 ? bar.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], { duration: this.nextAt - now, easing: 'linear', fill: 'forwards' }) : null;
      if (spot) this.ringCard(this.items[this.spot]);
    }

    // The spotlit card's circle of light: drawn round its symbol from the top, whole a moment, then fading out as it
    // widens a little (as on the arc).
    ringCard(item) {
      const ring = item?.card?.ring;
      if (!ring) return;
      ring.getAnimations({ subtree: true }).forEach(animation => animation.cancel());
      ring.firstElementChild.firstElementChild.animate([{ strokeDashoffset: 100, easing: 'cubic-bezier(.33,1,.68,1)' }, { strokeDashoffset: 0, offset: .36 }, { strokeDashoffset: 0 }], { duration: RING_MS, easing: 'linear' });
      ring.animate([{ opacity: 1, transform: 'scale(1)' }, { opacity: 1, transform: 'scale(1)', offset: .52 }, { opacity: 0, transform: 'scale(1.08)' }], { duration: RING_MS, easing: 'ease-out' });
    }

    // Brings card i to the middle the short way round; a visitor's choice holds the row there a while.
    goTo(index, byVisitor) {
      if (!this.cardGeo) return;
      const now = performance.now();
      if (byVisitor) { this.pauseUntil = now + HOLD_MS; this.intro = null; }
      const N = this.items.length;
      const r = this.cardPlace(((index % N) + N) % N);
      if (Math.abs(r) < .001 && !this.move) { this.settle(now); this.wake(); return; }
      this.moveTo(this.pos + r, now, MOVE_MS * Math.min(1.6, .7 + .3 * Math.abs(r)), inOut);
      this.wake();
    }

    tickCards(now) {
      this.frame = 0;
      clearTimeout(this.cardTimer);
      if (!this.live || this.mode !== 'cards' || !this.cardGeo) return;
      const running = this.visible && !document.hidden;
      const dragging = this.gesture?.state === 'drag';
      const N = this.items.length;
      if (this.move && !dragging) {
        const t = clamp((now - this.move.start) / this.move.duration);
        this.pos = this.move.from + (this.move.to - this.move.from) * this.move.ease(t);
        if (t >= 1) {
          this.pos = this.move.to;
          this.move = null;
          // Round the whole row once, quickly, and the first time is over.
          if (this.intro && --this.intro.moves <= 0) this.intro = null;
          this.settle(now);
        }
      } else if (!this.move && !dragging && running && N > 1) {
        if (!this.nextAt) this.settle(now);
        else if (now >= this.nextAt) this.moveTo(Math.round(this.pos) + 1, now, this.intro ? INTRO_MOVE_MS : MOVE_MS, inOut);
      }
      this.renderCards();
      if (!dragging && Math.abs(this.pos - Math.round(this.pos)) < .3) this.showCard(this.nearestCard());
      if (!running) return;
      // Frames only while the row moves; while a card waits in the middle a timer wakes the row when it is time.
      if (this.move || dragging) this.frame = requestAnimationFrame(time => this.tick(time));
      else if (this.nextAt) this.cardTimer = setTimeout(() => this.wake(), Math.max(16, this.nextAt - performance.now()));
    }

    cardsDown(event) {
      if (event.button !== 0 || event.target.closest('[data-card-step]')) return;
      const item = event.target.closest('.orion-feature-arc__item');
      this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, lastX: event.clientX, state: 'pending', item: item ? Number(item.dataset.index) : null, samples: [] };
    }

    cardsMove(event) {
      const gesture = this.gesture;
      if (!gesture || event.pointerId !== gesture.id) return;
      if (gesture.state === 'pending') {
        const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
        if (Math.hypot(dx, dy) < 7) return;
        // Up or down is the page's own scroll.
        if (event.pointerType !== 'mouse' && Math.abs(dy) > Math.abs(dx)) { this.gesture = null; return; }
        gesture.state = 'drag';
        gesture.lastX = event.clientX;
        this.setPointerCapture(event.pointerId);
        this.classList.add('is-dragging');
        this.move = null;
        this.nextAt = 0;
        this.fill?.cancel();
        this.fill = null;
        this.pauseUntil = performance.now() + HOLD_MS;
        this.intro = null;
        this.wake();
        return;
      }
      if (gesture.state !== 'drag') return;
      // The row follows the finger one to one.
      this.pos -= (event.clientX - gesture.lastX) / this.cardGeo.spacing;
      gesture.lastX = event.clientX;
      const now = performance.now();
      gesture.samples.push({ t: now, pos: this.pos });
      while (gesture.samples.length > 2 && now - gesture.samples[0].t > 90) gesture.samples.shift();
      this.wake();
    }

    cardsUp(event, cancelled) {
      const gesture = this.gesture;
      if (!gesture || event.pointerId !== gesture.id) return;
      this.gesture = null;
      this.classList.remove('is-dragging');
      const now = performance.now();
      if (gesture.state === 'drag') {
        // Let go: a flick goes on to the next card that way, otherwise the nearest card comes to the middle.
        const { samples } = gesture;
        let velocity = 0;
        if (!cancelled && samples.length > 1) {
          const first = samples[0], last = samples[samples.length - 1];
          if (now - last.t < 60 && last.t > first.t) velocity = (last.pos - first.pos) / (last.t - first.t);
        }
        const target = Math.abs(velocity) > .0012 ? (velocity > 0 ? Math.floor(this.pos) + 1 : Math.ceil(this.pos) - 1) : Math.round(this.pos);
        this.pauseUntil = now + HOLD_MS;
        this.moveTo(target, now, 560, outCubic);
      } else if (!cancelled && gesture.item !== null) {
        this.goTo(gesture.item, true);
      }
      this.wake();
    }
  }

  customElements.define('orion-feature-arc', OrionFeatureArc);

  // ------------------------------------------------------------------------------------------------ The full list
  // The button next to the title opens a window with every material (left) and feature (right), icon and name,
  // taken from the arc itself so the two always match.
  const fill = dialog => {
    const arc = document.getElementById(dialog.dataset.arc);
    if (!arc) return;
    const columns = [...dialog.querySelectorAll('[data-list-kind]')];
    columns.forEach(column => { column.querySelector('ul').textContent = ''; });
    arc.querySelectorAll('.orion-feature-arc__item').forEach(item => {
      const column = columns.find(entry => entry.dataset.listKind === item.dataset.kind);
      if (!column) return;
      const li = document.createElement('li');
      const icon = item.querySelector('img:not(.orion-feature-arc__icon--lit)');
      if (icon) {
        const copy = icon.cloneNode();
        copy.removeAttribute('class');
        copy.removeAttribute('style');
        copy.alt = '';
        copy.loading = 'eager';
        li.append(copy);
      }
      const name = document.createElement('span');
      name.textContent = item.dataset.title || item.getAttribute('aria-label') || '';
      li.append(name);
      // A symbol that opens a scene of its own (Cool Max) opens it from the list too.
      if (item.hasAttribute('data-orion-coolmax-open')) {
        li.dataset.orionCoolmaxOpen = '';
        li.tabIndex = 0;
        li.setAttribute('role', 'button');
      }
      column.querySelector('ul').append(li);
    });
    columns.forEach(column => { column.hidden = !column.querySelector('li'); });
    dialog.classList.toggle('is-single', columns.filter(column => !column.hidden).length < 2);
  };

  const open = dialog => {
    if (typeof dialog.showModal !== 'function' || dialog.open) return;
    fill(dialog);
    dialog.showModal();
    document.documentElement.classList.add('orion-feature-list-open');
    dialog.animate([{ opacity: 0, transform: 'translateY(14px)' }, { opacity: 1, transform: 'none' }], { duration: 320, easing: 'cubic-bezier(.22,.68,.21,1)' });
    try { dialog.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 320, pseudoElement: '::backdrop' }); } catch (error) { /* older browsers: no fade */ }
  };

  const close = dialog => {
    if (!dialog.open || dialog.dataset.closing) return;
    dialog.dataset.closing = 'true';
    try { dialog.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, pseudoElement: '::backdrop', fill: 'forwards' }); } catch (error) { /* older browsers: no fade */ }
    dialog.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(10px)' }], { duration: 200, easing: 'ease-in', fill: 'forwards' })
      .finished.catch(() => {}).then(() => {
        delete dialog.dataset.closing;
        dialog.getAnimations().forEach(animation => animation.cancel());
        dialog.close();
      });
  };

  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-feature-list-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('aria-controls'));
      if (dialog) open(dialog);
      return;
    }
    if (event.target.closest('[data-feature-list-close]')) {
      close(event.target.closest('dialog'));
      return;
    }
    // A click on the dim area round the window closes it.
    const dialog = event.target.closest('dialog.orion-feature-list');
    if (dialog && event.target === dialog) {
      const box = dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) close(dialog);
    }
  });
  document.addEventListener('cancel', event => {
    if (!event.target.matches || !event.target.matches('dialog.orion-feature-list')) return;
    event.preventDefault();
    close(event.target);
  }, true);
  document.addEventListener('close', event => {
    if (event.target.matches && event.target.matches('dialog.orion-feature-list')) document.documentElement.classList.remove('orion-feature-list-open');
  }, true);
})();
